/**
 * The slip as the parent page sees it (SPORT_SLIP_CHANGED): one entry per leg,
 * plus the bet type, the stake and the potential win of the whole ticket.
 *
 * A bet builder group keeps the cart convention, which is also the one of the B2B
 * `tickets` call: the first leg carries the combined price, the other legs 1.
 * count follows the cart badge: a bet builder group counts once.
 * Ids are strings; a manual market keeps its M-prefixed id.
 * odds is always decimal; oddsDisplay is the same odds in the format the player
 * chose (oddsFormat), as the slip shows it.
 */

import { convertOddsByType, canonicalOddsFormat } from '@/services/oddsFormat';

const idOrNull = (value) => (value === undefined || value === null || value === '' ? null : String(value));

// Placeholder category some widgets put in the cart when the event has none (StartingSoonView & co.)
const PLACEHOLDER_CATEGORY_ID = 'best_cat';

const numberOrNull = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/**
 * Selections as the cart badge counts them: a bet builder group is one selection.
 *
 * @param {object[]} odds - state.cart.bet.odds
 * @returns {number}
 */
export function countSelections(odds) {
    const groups = new Set(odds.filter(o => o.bet_builder_id).map(o => o.bet_builder_id));
    return odds.filter(o => !o.bet_builder_id).length + groups.size;
}

/**
 * Stake and potential win of the whole ticket, read from the detail of its bet type
 * (the same fields cart/placeBet and the confirmed bet use).
 *
 * @param {object} bet - state.cart.bet
 * @returns {{stake: number|null, potentialWin: number|null}}
 */
export function ticketTotals(bet) {
    switch (bet.type) {
        case 'integral': {
            const detail = bet._integral_detail || {};
            return { stake: numberOrNull(detail.amount_total), potentialWin: numberOrNull(detail.winning_total_max) };
        }
        case 'system': {
            const detail = bet._system_detail || {};
            return { stake: numberOrNull(detail.amount_system), potentialWin: numberOrNull(detail.winning_total_max) };
        }
        default: {
            const detail = bet._multiple_detail || {};
            return { stake: numberOrNull(detail.amount), potentialWin: numberOrNull(detail.winning_total) };
        }
    }
}

/**
 * The handicap/total line of a leg, the way the bet receipt shows it
 * (BetConfirmedMarketView getOddLine): line, then baseLine, then the first
 * specifier token when it is a number.
 */
export function selectionLine(odd) {
    if (odd.line) return String(odd.line);
    if (odd.baseLine) return String(odd.baseLine);
    if (odd.specifiers) {
        const part = String(odd.specifiers).split(' ')[0];
        const first = part.charAt(0);
        return isNaN(first) && first !== '-' && first !== '+' ? null : part;
    }
    return null;
}

/**
 * What the cart knows about the leg after a placement: closed (ODD_DISABLED, or update_odds
 * disabled / time_over → to_remove), suspended (ODD_SUSPENDED, cleared when the socket
 * reopens it) or open.
 */
export function selectionStatus(odd) {
    if (odd.disabled || odd.to_remove) return 'closed';
    if (odd.suspended) return 'suspended';
    return 'open';
}

/**
 * A price change waiting for the player's acceptance, null otherwise. MARK_ODD_CHANGED keeps
 * both values on the leg (oldOdd / newOdd); update_odds replaces the leg with the server's one
 * (changed: true, new value only), so the old value is the one of the last slip posted.
 */
function oddsChangeOf(odd, previousOdds) {
    if (!odd.changed) return null;
    const selectionId = idOrNull(odd.unique_id);
    const previous = previousOdds && selectionId !== null ? previousOdds[selectionId] : undefined;
    return {
        from: odd.oldOdd != null ? numberOrNull(odd.oldOdd) : (previous != null ? previous : null),
        to:   odd.newOdd != null ? numberOrNull(odd.newOdd) : numberOrNull(odd.outcome_odd_value),
    };
}

function categoryIdOf(odd) {
    const id = odd.category && odd.category.id;
    return id === PLACEHOLDER_CATEGORY_ID ? null : idOrNull(id);
}

function toSelection(odd, previousOdds, oddsFormat) {
    const decimal = numberOrNull(odd.outcome_odd_value);
    return {
        selectionId:  idOrNull(odd.unique_id),
        eventId:      idOrNull(odd.event_source),
        eventName:    odd.event_description || null,
        sportId:      idOrNull(odd.sport && odd.sport.id),
        categoryId:   categoryIdOf(odd),
        tournamentId: idOrNull(odd.tournament && odd.tournament.id),
        marketId:     idOrNull(odd.id_market),
        marketName:   odd.market_alias || odd.market_name || null,
        outcomeName:  odd.outcome_alias || odd.outcome_name || null,
        line:         selectionLine(odd),
        odds:         decimal,
        oddsDisplay:  decimal === null ? null : String(convertOddsByType(decimal, oddsFormat)),
        status:       selectionStatus(odd),
        oddsChanged:  oddsChangeOf(odd, previousOdds),
        isLive:       odd.type === 'live',
        betBuilderId: odd.bet_builder_id || null,
    };
}

/**
 * @param {object} bet - state.cart.bet
 * @param {Object<string, number>} [previousOdds] - odds of the last slip posted, by selectionId
 * @param {string} [oddsType] - odds/getOddsType, the format the player chose (decimal when missing)
 * @returns {{count: number, betType: string|null, oddsFormat: string, stake: number|null, potentialWin: number|null, selections: object[]}}
 */
export function buildSlipSnapshot(bet, previousOdds, oddsType) {
    const odds = bet && Array.isArray(bet.odds) ? bet.odds : [];
    const totals = odds.length > 0 ? ticketTotals(bet) : { stake: null, potentialWin: null };
    const oddsFormat = canonicalOddsFormat(oddsType);
    return {
        count:        countSelections(odds),
        betType:      (bet && bet.type) || null,
        oddsFormat,
        stake:        totals.stake,
        potentialWin: totals.potentialWin,
        selections:   odds.map(odd => toSelection(odd, previousOdds, oddsFormat)),
    };
}

/**
 * The odds to remember after posting a snapshot: a leg with a pending change keeps the value
 * it had before the change, so that `from` does not move until the player accepts.
 *
 * @param {object} snapshot - buildSlipSnapshot result
 * @param {Object<string, number>} previousOdds
 * @returns {Object<string, number>}
 */
export function oddsToRemember(snapshot, previousOdds) {
    const next = {};
    for (const sel of snapshot.selections) {
        if (sel.selectionId === null) continue;
        next[sel.selectionId] = sel.oddsChanged ? (previousOdds || {})[sel.selectionId] : sel.odds;
    }
    return next;
}
